import { ReactNode, useCallback, useMemo } from 'react';

import { DndContext } from '@dnd-kit/core';
import { SortableContext } from '@dnd-kit/sortable';
import styled from 'styled-components';

import { Badge } from '@components/badge';
import { Breadcrumb } from '@components/breadcrumb';
import { Button } from '@components/button';
import { Card } from '@components/card';
import { PortalDragOverlay } from '@components/drag-and-drop';
import {
  OverlayContainer,
  useSortable,
  useSortableDefaults,
} from '@components/drag-and-drop/defaults';
import { useDeleteModal } from '@components/generic-modals';
import { TabsHeader } from '@components/header/tabs-header';
import { IconButton } from '@components/icon-button';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconDate } from '@components/icons/date';
import { IconDrag } from '@components/icons/drag';
import { IconEdit } from '@components/icons/edit';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconStar } from '@components/icons/star';
import { IconTrash } from '@components/icons/trash';
import { Spinner } from '@components/loading-spinner';
import { Menu } from '@components/menu';
import { PageContainers } from '@components/page-containers';
import { PageContent } from '@components/page-containers/page-container';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useFormatPickerModal } from '@components/text-editors/addons/format-picker-modal';
import {
  FORMAT_LANGUAGE_OPTIONS,
  getFormatPreview,
} from '@components/text-editors/addons/format-presets';
import { showSuccess } from '@components/toaster';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { uuid } from '@utils/uuid';

import { useCompanyDetails, useEditCompanyDetails } from 'api/company/hooks';
import { CompanyFormatPreset } from 'api/company/requests';

import { SettingsSection } from './general';

type UpdatePresets = (args: { presets: CompanyFormatPreset[]; successMessage?: string }) => void;

const useDatePresetsMutation = (): {
  presets: CompanyFormatPreset[];
  updatePresets: UpdatePresets;
  isLoading: boolean;
} => {
  const { data: company } = useCompanyDetails();
  const { mutate: editCompany } = useEditCompanyDetails({ shouldNotShowSuccess: true });

  const allPresets = company?.formatPresets;
  const presets = useMemo(() => allPresets?.date ?? [], [allPresets]);

  const updatePresets = useCallback<UpdatePresets>(
    ({ presets: updated, successMessage }) => {
      editCompany(
        { formatPresets: { ...allPresets, date: updated } },
        {
          onSuccess: () => {
            if (successMessage) showSuccess(successMessage);
          },
        },
      );
    },
    [allPresets, editCompany],
  );

  return { presets, updatePresets, isLoading: !company };
};

const getLanguageLabel = (languageValue?: string): string | undefined => {
  if (!languageValue) return undefined;
  return FORMAT_LANGUAGE_OPTIONS.find((option) => option.value === languageValue)?.label;
};

const withSingleDefault = (
  presets: CompanyFormatPreset[],
  defaultId: string,
): CompanyFormatPreset[] => {
  return presets.map((preset) => ({ ...preset, isDefault: preset.id === defaultId }));
};

type PresetItemProps = {
  preset: CompanyFormatPreset;
  onMakeDefault: (id: string) => void;
  onEdit: (preset: CompanyFormatPreset) => void;
  onRemove: (preset: CompanyFormatPreset) => void;
};

const PresetRow = ({
  preset,
  isOverlay,
  children,
}: {
  preset: CompanyFormatPreset;
  isOverlay?: boolean;
  children?: ReactNode;
}) => {
  const preview = getFormatPreview(preset.format, preset.formatLanguage);

  return (
    <Stack orientation="horizontal" align="center" justify="space-between">
      <Stack orientation="horizontal" align="center" space={3}>
        <DragHandle $isOverlay={isOverlay}>
          <IconDrag color="elementPurple50" />
        </DragHandle>
        <IconDate color="elementPurple50" size={20} />
        <Stack space={1}>
          <Text text={preset.name} font="bold14" />
          <Text text={preview} font="regular14" color="textSecondary" />
        </Stack>
      </Stack>
      {children}
    </Stack>
  );
};

const PresetItem = ({ preset, onMakeDefault, onEdit, onRemove }: PresetItemProps) => {
  const { t } = useScopedTranslate(locale);
  const { attributes, listeners, setDraggableNodeRef, setDroppableNodeRef, style } = useSortable({
    id: preset.id,
  });
  const languageLabel = getLanguageLabel(preset.formatLanguage);

  return (
    <div ref={setDroppableNodeRef}>
      <div style={style} ref={setDraggableNodeRef}>
        <Card space={3} interactive style={{ cursor: 'default' }} {...attributes} {...listeners}>
          <PresetRow preset={preset}>
            <Stack orientation="horizontal" space={2} align="center">
              {languageLabel && <Badge text={languageLabel} variant="green" size="sm" />}
              {preset.isDefault && <Badge text={t('default')} variant="blue" size="sm" />}
              <Menu
                trigger={IconButton}
                triggerProps={{
                  variant: 'ghost-purple',
                  icon: <IconMoreVertical />,
                  stopPropagation: true,
                }}
                placement="bottom-end"
              >
                <Menu.Item
                  label={t('makeDefault')}
                  icon={<IconStar />}
                  isDisabled={preset.isDefault}
                  onSelect={() => onMakeDefault(preset.id)}
                />
                {!preset.isSystem && (
                  <>
                    <Menu.Item
                      label={t('edit')}
                      icon={<IconEdit />}
                      onSelect={() => onEdit(preset)}
                    />
                    <Menu.Separator />
                    <Menu.Item
                      label={t('delete')}
                      icon={<IconTrash />}
                      variant="danger"
                      isDisabled={preset.isDefault}
                      tooltip={preset.isDefault ? t('deleteDefaultTooltip') : undefined}
                      onSelect={() => onRemove(preset)}
                    />
                  </>
                )}
              </Menu>
            </Stack>
          </PresetRow>
        </Card>
      </div>
    </div>
  );
};

const PresetDragOverlay = ({ preset }: { preset: CompanyFormatPreset }) => {
  const languageLabel = getLanguageLabel(preset.formatLanguage);
  return (
    <OverlayContainer $borderRadius={8}>
      <Card space={3} style={{ backgroundColor: theme.color.backgroundLightDarkHover }}>
        <PresetRow preset={preset} isOverlay>
          {languageLabel && <Badge text={languageLabel} variant="green" size="sm" />}
        </PresetRow>
      </Card>
    </OverlayContainer>
  );
};

type DateFormatsTabProps = {
  presets: CompanyFormatPreset[];
  updatePresets: UpdatePresets;
};

const DateFormatsTab = ({ presets, updatePresets }: DateFormatsTabProps) => {
  const { t } = useScopedTranslate(locale);
  const showFormatPicker = useFormatPickerModal();
  const showDeleteModal = useDeleteModal();

  const {
    items,
    itemIds,
    activeItem,
    handleDragEnd,
    handleDragStart,
    handleDragOver,
    strategy,
    ...sortingDefaults
  } = useSortableDefaults({
    items: presets,
    onDragEnd: (reordered) => updatePresets({ presets: reordered }),
  });

  const handleMakeDefault = (id: string) => {
    updatePresets({
      presets: withSingleDefault(presets, id),
      successMessage: t('defaultUpdated'),
    });
  };

  const handleCreate = () => {
    showFormatPicker({
      onApply: () => {},
      onSavePreset: (preset) => {
        const newPreset: CompanyFormatPreset = { ...preset, id: uuid() };
        updatePresets({ presets: [...presets, newPreset], successMessage: t('formatAdded') });
      },
    });
  };

  const handleEdit = (preset: CompanyFormatPreset) => {
    showFormatPicker({
      isEditing: true,
      initialFormat: preset.format,
      initialLanguage: preset.formatLanguage,
      initialName: preset.name,
      onApply: (format, formatLanguage, name) => {
        const updated = presets.map((presetItem) =>
          presetItem.id === preset.id
            ? { ...presetItem, format, formatLanguage, name: name ?? presetItem.name }
            : presetItem,
        );
        updatePresets({ presets: updated });
      },
    });
  };

  const handleRemove = (preset: CompanyFormatPreset) => {
    showDeleteModal({
      title: t('deleteTitle'),
      description: t('deleteDescription'),
      hasUserConfirmation: true,
      onConfirm: () => {
        const remaining = presets.filter((presetItem) => presetItem.id !== preset.id);
        const next =
          preset.isDefault && remaining.length > 0
            ? withSingleDefault(remaining, remaining[0].id)
            : remaining;
        updatePresets({ presets: next });
      },
    });
  };

  return (
    <Stack space={6}>
      <SettingsSection title={t('dateFormats')} description={t('dateDescription')}>
        <Stack space={3}>
          <DndContext
            {...sortingDefaults}
            onDragEnd={handleDragEnd}
            onDragOver={handleDragOver}
            onDragStart={handleDragStart}
          >
            <SortableContext items={itemIds} strategy={strategy}>
              <Stack space={2}>
                {items.map((preset) => (
                  <PresetItem
                    key={preset.id}
                    preset={preset}
                    onMakeDefault={handleMakeDefault}
                    onEdit={handleEdit}
                    onRemove={handleRemove}
                  />
                ))}
              </Stack>
            </SortableContext>
            <PortalDragOverlay dropAnimation={null}>
              {activeItem && <PresetDragOverlay preset={activeItem} />}
            </PortalDragOverlay>
          </DndContext>
          <div>
            <Button
              variant="secondary"
              text={t('addFormat')}
              iconLeft={<IconAddPlus />}
              onClick={handleCreate}
            />
          </div>
        </Stack>
      </SettingsSection>
    </Stack>
  );
};

export const FormatPresets = () => {
  const { t } = useScopedTranslate(locale);
  const { presets, updatePresets, isLoading } = useDatePresetsMutation();

  if (isLoading) {
    return <Spinner />;
  }

  const tabs = [
    {
      id: 'date',
      label: t('date'),
      icon: <IconDate />,
      counter: presets.length,
      content: (
        <PageContent>
          <PageContainers.MiddleColumnPage>
            <DateFormatsTab presets={presets} updatePresets={updatePresets} />
          </PageContainers.MiddleColumnPage>
        </PageContent>
      ),
    },
  ];

  return <TabsHeader breadcrumbs={<Breadcrumb items={[{ label: t('title') }]} />} tabs={tabs} />;
};

const DragHandle = styled(Stack).attrs({ align: 'center', justify: 'center' })<{
  $isOverlay?: boolean;
}>(({ $isOverlay }) => ({
  width: pxToRem(24),
  minWidth: pxToRem(24),
  cursor: $isOverlay ? 'grabbing' : 'grab',
}));

const locale = {
  en: {
    title: 'Formats',
    date: 'Date',
    default: 'Default',
    dateFormats: 'Date formats',
    dateDescription: 'Manage available date format presets and defaults across the system',
    addFormat: 'Add date format',
    makeDefault: 'Make default',
    edit: 'Edit',
    delete: 'Delete',
    deleteTitle: 'Delete format',
    deleteDescription:
      'Are you sure you want to remove this format? Anywhere this format is used will be reverted to use your default format instead',
    deleteDefaultTooltip: 'Set another format as default before deleting this one',
    formatAdded: 'New date format added',
    defaultUpdated: 'Default date format updated',
  },
};
